import { onMounted, onBeforeUnmount, watch, type Ref } from 'vue'
import type { CreateDraftCachePayload } from '@/utils/createDraftCache'
import {
  registerSaveDraftCacheGetter,
  unregisterSaveDraftCacheGetter,
  registerRestoreDraftCache,
  unregisterRestoreDraftCache,
  installDraftCacheBridge,
  restoreOutlineDraftOnEnter,
  saveOutlineDraftToStorage,
  applyOutlineDraftGlobally,
} from '@/utils/createDraftCache'

export interface OutlineDefaultOptions {
  pageCount: string
  language: string
  audiences: string
  textVolume: string
  scene: string
}

interface LocalFileInfo {
  name: string
  status: string
}

interface FileStoreLike {
  setUploadedFile: (fileInfo: { name: string; fujian: string }) => void
  clearFile: () => void
}

interface OutlineParamsLike {
  inputText: string
  pageCount: string
  language: string
  audiences: string
  textVolume: string
  scene: string
  fujian: string
  pics?: string[]
}

/**
 * 注册首页 / 模板详情等页面的「立即生成」草稿缓存桥接。
 * 页面挂载时注册 getter/restore，卸载时注销，避免多页面互相覆盖。
 * 输入变化时写入 localStorage，供其他原生 Tab 的 WebView 读取。
 */
export function useCreateDraftCache(options: {
  outlineParams: Ref<OutlineParamsLike>
  uploadedFile: Ref<{ name?: string; fujian?: string } | null>
  localFileInfo: Ref<LocalFileInfo | null>
  defaultOutlineOptions: OutlineDefaultOptions
  fileStore: FileStoreLike
  /** 页面进入时从原生 / localStorage 拉取并恢复草稿 */
  fetchOnMount?: boolean
}) {
  const {
    outlineParams,
    uploadedFile,
    localFileInfo,
    defaultOutlineOptions,
    fileStore,
    fetchOnMount = false,
  } = options

  const buildCreateDraftCachePayload = (): CreateDraftCachePayload => ({
    inputText: outlineParams.value.inputText || '',
    pageCount: outlineParams.value.pageCount || defaultOutlineOptions.pageCount,
    language: outlineParams.value.language || defaultOutlineOptions.language,
    audiences: outlineParams.value.audiences || defaultOutlineOptions.audiences,
    textVolume: outlineParams.value.textVolume || defaultOutlineOptions.textVolume,
    scene: outlineParams.value.scene || defaultOutlineOptions.scene,
    fujian: outlineParams.value.fujian || uploadedFile.value?.fujian || '',
    fileName: localFileInfo.value?.name || uploadedFile.value?.name || '',
    pics: [...(outlineParams.value.pics || [])],
  })

  const applyCreateDraftCache = (data: CreateDraftCachePayload) => {
    if (!data) return
    applyOutlineDraftGlobally(data)

    if (data.fujian && data.fileName) {
      localFileInfo.value = { name: data.fileName, status: 'success' }
    } else if (!data.fujian) {
      localFileInfo.value = null
    }
  }

  const persistOutlineDraft = () => {
    saveOutlineDraftToStorage(buildCreateDraftCachePayload())
  }

  const setup = () => {
    installDraftCacheBridge()
    registerSaveDraftCacheGetter(buildCreateDraftCachePayload)
    registerRestoreDraftCache(applyCreateDraftCache)
  }

  const teardown = () => {
    unregisterSaveDraftCacheGetter()
    unregisterRestoreDraftCache()
  }

  watch([outlineParams, uploadedFile, localFileInfo], persistOutlineDraft, { deep: true })

  onMounted(() => {
    setup()
    if (fetchOnMount) {
      restoreOutlineDraftOnEnter()
    }
  })
  onBeforeUnmount(teardown)

  return { buildCreateDraftCachePayload, applyCreateDraftCache, setup, teardown, persistOutlineDraft }
}
